import { setLocalStorageItem, getLocalStorageItem, removeLocalStorageItem } from "../../utils";
import { USER_SESSION_KEY } from "@hooks/use-auth-context/utils";

/**
 * Makes a fetch request with automatic JWT token refresh on 401.
 * Side effects: reads, writes, and may remove the user session from localStorage.
 * If the request gets a 401, uses the refresh token to get a new auth token,
 * then retries the original request.
 * @param onTokenRefreshed - Optional callback invoked with the new token data after
 *   a successful reactive refresh, allowing callers to sync React state.
 */
export async function fetchWithTokenRefresh(
  url: string,
  options: RequestInit = {},
  refreshTokenFn?: (() => Promise<{ authToken: string; authTokenExpiration: string } | null | undefined>) | null,
  onTokenRefreshed?: (newToken: { authToken: string; authTokenExpiration: string }) => void
): Promise<Response> {
  // Pre-request: ensure we send the freshest possible token.
  // Two cases handled:
  //   1. Token is expired → refresh before sending (avoids backends that return 500 for auth failures).
  //   2. Proactive refresh already ran and updated localStorage with a newer token than what the
  //      React-state closure captured → swap the header to the localStorage token so we never send
  //      an invalidated token.
  let effectiveOptions = options;
  if (refreshTokenFn) {
    const storedSession = getLocalStorageItem(USER_SESSION_KEY);
    if (storedSession?.authToken && storedSession?.authTokenExpiration) {
      const expiresAtMs = parseInt(storedSession.authTokenExpiration) * 1000;
      const headerToken = ((options.headers as Record<string, string>)?.Authorization ?? "").replace("Bearer ", "");

      if (Date.now() >= expiresAtMs) {
        // Token is expired — refresh now
        try {
          const newTokenData = await refreshTokenFn();
          if (newTokenData?.authToken) {
            const updatedSession = { ...storedSession, authToken: newTokenData.authToken, authTokenExpiration: newTokenData.authTokenExpiration };
            setLocalStorageItem(USER_SESSION_KEY, updatedSession);
            onTokenRefreshed?.(newTokenData as { authToken: string; authTokenExpiration: string });
            const headers = { ...((effectiveOptions.headers as Record<string, string>) ?? {}) };
            headers.Authorization = `Bearer ${newTokenData.authToken}`;
            effectiveOptions = { ...effectiveOptions, headers };
          }
        } catch {
          // Proceed with the original (expired) token; the 401 handler below will attempt another refresh
        }
      } else if (storedSession.authToken !== headerToken) {
        // localStorage has a newer valid token than the React-state closure — use it
        const headers = { ...((effectiveOptions.headers as Record<string, string>) ?? {}) };
        headers.Authorization = `Bearer ${storedSession.authToken}`;
        effectiveOptions = { ...effectiveOptions, headers };
      }
    }
  }

  let response = await fetch(url, effectiveOptions);

  // If 401 and we have a way to refresh, attempt token refresh
  if (response.status === 401 && refreshTokenFn) {
    try {
      const newTokenData = await refreshTokenFn();

      if (newTokenData?.authToken) {
        // Update stored session with new token
        const storedSession = getLocalStorageItem(USER_SESSION_KEY);
        if (storedSession) {
          const updatedSession = {
            ...storedSession,
            authToken: newTokenData.authToken,
            authTokenExpiration: newTokenData.authTokenExpiration,
          };
          setLocalStorageItem(USER_SESSION_KEY, updatedSession);
        }

        // Notify caller so React state can be synced without a full page cycle
        onTokenRefreshed?.(newTokenData as { authToken: string; authTokenExpiration: string });

        // Update auth header and retry
        const updatedOptions = { ...options };
        const headers = { ...(updatedOptions.headers as Record<string, string> || {}) };
        headers.Authorization = `Bearer ${newTokenData.authToken}`;
        updatedOptions.headers = headers;

        response = await fetch(url, updatedOptions);
      }
    } catch (err) {
      // If refresh fails, return the original 401 response
    }
  }

  // If still 401 after refresh attempt, clear session (tokens are no longer valid)
  if (response.status === 401) {
    removeLocalStorageItem(USER_SESSION_KEY);
  }

  return response;
}
